import { useMemo, useState } from 'react'
import {
  Check,
  ChevronsUpDown,
  CircleAlert,
  GitBranch,
  Sparkles,
  Tag,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { $api } from '@/api/client'
import { describeError } from '@/lib/api-error'
import type { components } from '@/api/openapi'
import { cn } from '@/lib/utils'

type GitHubRef = components['schemas']['GitHubRef']

type RefComboboxProps = {
  repo: string
  value: string
  onChange: (next: string) => void
  placeholder?: string
  className?: string
  triggerClassName?: string
  contentWidth?: string
  popoverContainer?: HTMLElement | null
  ['aria-invalid']?: boolean
}

const REPO_SLUG = /^[A-Za-z0-9._-]+\/[A-Za-z0-9._-]+$/

function refMatches(r: GitHubRef, q: string): boolean {
  if (!q) return true
  return r.name.toLowerCase().includes(q.toLowerCase())
}

export function RefCombobox({
  repo,
  value,
  onChange,
  placeholder = 'main',
  className,
  triggerClassName,
  contentWidth = 'var(--radix-popover-trigger-width)',
  popoverContainer,
  'aria-invalid': ariaInvalid,
}: RefComboboxProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')

  const repoSlug = repo.trim()
  const validRepo = REPO_SLUG.test(repoSlug)
  const disabled = !validRepo

  const refsQuery = $api.useQuery(
    'get',
    '/github/refs',
    { params: { query: { repo: repoSlug } } },
    {
      enabled: open && validRepo,
      staleTime: 30_000,
      refetchOnMount: false,
    },
  )

  const branches = refsQuery.data?.branches ?? []
  const tags = refsQuery.data?.tags ?? []
  const trimmedSearch = search.trim()

  const filteredBranches = useMemo(
    () => (trimmedSearch ? branches.filter((b) => refMatches(b, trimmedSearch)) : branches),
    [branches, trimmedSearch],
  )
  const filteredTags = useMemo(
    () => (trimmedSearch ? tags.filter((t) => refMatches(t, trimmedSearch)) : tags),
    [tags, trimmedSearch],
  )

  const allKnown = useMemo(() => {
    const set = new Set<string>()
    for (const b of branches) set.add(b.name)
    for (const t of tags) set.add(t.name)
    return set
  }, [branches, tags])

  const exactMatch = trimmedSearch ? allKnown.has(trimmedSearch) : false
  const showCustomRow = trimmedSearch.length > 0 && !exactMatch

  const submitCustom = () => {
    const v = trimmedSearch
    if (!v) return
    onChange(v)
    setOpen(false)
    setSearch('')
  }

  const triggerLabel = !value ? (
    <span className="text-muted-foreground font-sans text-sm truncate">
      {disabled ? 'Select a repository first' : placeholder}
    </span>
  ) : (
    <span className="font-mono text-[12.5px] truncate">{value}</span>
  )

  const noResults =
    !refsQuery.isLoading &&
    !refsQuery.isError &&
    filteredBranches.length === 0 &&
    filteredTags.length === 0 &&
    !showCustomRow

  return (
    <div className={cn('flex w-full', className)}>
      <Popover
        open={open && !disabled}
        onOpenChange={(next) => !disabled && setOpen(next)}
      >
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-invalid={ariaInvalid || undefined}
            disabled={disabled}
            className={cn(
              'h-9 w-full justify-between gap-2 px-3 font-normal',
              triggerClassName,
            )}
          >
            <div className="flex min-w-0 items-center gap-2">
              <GitBranch className="size-3.5 shrink-0 text-muted-foreground" />
              {triggerLabel}
            </div>
            <ChevronsUpDown className="ml-2 size-3.5 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          sideOffset={4}
          className="p-0"
          style={{ width: contentWidth, minWidth: '20rem' }}
          container={popoverContainer ?? null}
        >
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search branches, tags, or paste SHA…"
              value={search}
              onValueChange={setSearch}
            />
            <CommandList className="max-h-[300px]">
              {refsQuery.isLoading ? (
                <div className="space-y-2 px-3 py-4 text-xs text-muted-foreground">
                  <div className="shimmer h-4 w-2/3 rounded-md bg-muted" />
                  <div className="shimmer h-4 w-1/2 rounded-md bg-muted" />
                </div>
              ) : null}

              {refsQuery.isError ? (
                <div className="flex items-start gap-2 px-3 py-3 text-xs text-destructive">
                  <CircleAlert className="size-3.5 shrink-0 mt-0.5" />
                  <span>{describeError(refsQuery.error).message}</span>
                </div>
              ) : null}

              {noResults ? (
                <CommandEmpty>No refs in this repo.</CommandEmpty>
              ) : null}

              {filteredBranches.length > 0 ? (
                <CommandGroup heading="Branches">
                  {filteredBranches.map((b) => (
                    <CommandItem
                      key={`branch:${b.name}`}
                      value={`branch:${b.name}`}
                      onSelect={() => {
                        onChange(b.name)
                        setOpen(false)
                        setSearch('')
                      }}
                      className="gap-2 py-1.5"
                    >
                      <Check
                        className={cn(
                          'size-3.5 shrink-0',
                          value === b.name ? 'opacity-100' : 'opacity-0',
                        )}
                      />
                      <GitBranch className="size-3.5 shrink-0 text-muted-foreground" />
                      <span className="font-mono text-[12.5px] truncate">{b.name}</span>
                      <span className="ml-auto font-mono text-[10.5px] text-muted-foreground">
                        {b.sha.slice(0, 7)}
                      </span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              ) : null}

              {filteredTags.length > 0 ? (
                <CommandGroup heading="Tags">
                  {filteredTags.map((t) => (
                    <CommandItem
                      key={`tag:${t.name}`}
                      value={`tag:${t.name}`}
                      onSelect={() => {
                        onChange(t.name)
                        setOpen(false)
                        setSearch('')
                      }}
                      className="gap-2 py-1.5"
                    >
                      <Check
                        className={cn(
                          'size-3.5 shrink-0',
                          value === t.name ? 'opacity-100' : 'opacity-0',
                        )}
                      />
                      <Tag className="size-3.5 shrink-0 text-muted-foreground" />
                      <span className="font-mono text-[12.5px] truncate">{t.name}</span>
                      <span className="ml-auto font-mono text-[10.5px] text-muted-foreground">
                        {t.sha.slice(0, 7)}
                      </span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              ) : null}

              {showCustomRow ? (
                <CommandGroup heading="Use custom value">
                  <CommandItem
                    value={`__custom__:${trimmedSearch}`}
                    onSelect={submitCustom}
                    className="gap-2"
                  >
                    <Sparkles className="size-3.5 text-amber-500" />
                    <span className="text-sm">
                      Use{' '}
                      <span className="font-mono text-[12.5px] text-foreground">
                        {trimmedSearch}
                      </span>
                    </span>
                  </CommandItem>
                </CommandGroup>
              ) : null}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  )
}
